import { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";

import Stack from "@/components/stack/stack";
import {
  TimezoneTool,
  ToolbarRoot,
  ToolbarTitle,
} from "@/features/dashboard/components/toolbar/toolbar";
import toolbarStyles from "@/features/dashboard/components/toolbar/toolbar.module.css";
import { MaritimeWarningTool } from "@/features/dashboard/components/warning-tool/maritime-warning-tool";
import { LandWarningTool } from "@/features/dashboard/components/warning-tool/land-warning-tool";
import { RefreshButton } from "@/features/dashboard/features/refresh-timer/button";
import { WidgetRuntimeProvider } from "@/features/shared/widget-context/widget-runtime-context";
import { useDashboardWarningSettings } from "@/features/shared/hooks";
import { usePublicWarningPageDefaults } from "@/features/shared/hooks/use-warning-page-defaults";
import { usePublishedDashboardsQuery } from "@/features/shared/hooks/queries";
import { RiskPicture } from "@/features/shared/widgets/risk-picture/risk-picture";
import Timezone from "@/lib/timezone/Timezone";
import { readPublicWarningPageSettings } from "@/lib/public-warning-settings.utils";

import styles from "./warning-page.module.css";

export default function WarningPage() {
  const params = useParams();
  const dashboardsQuery = usePublishedDashboardsQuery();
  const { selectedDashboard, publicWarningSettings } = useDashboardWarningSettings();
  const page = readPublicWarningPageSettings(
    publicWarningSettings,
    usePublicWarningPageDefaults(),
  );
  const { title, description, staticOverlays } = page;
  const timezone = useMemo(
    () => Timezone.fromSettings(selectedDashboard?.time_zone_settings),
    [selectedDashboard?.time_zone_settings],
  );

  if (dashboardsQuery.isLoading) return null;

  if (!selectedDashboard || !page.enabled) {
    return <Navigate to={`/${params.dashboard ?? ""}`} replace />;
  }

  return (
    <WidgetRuntimeProvider timezone={timezone} autoRefreshInterval={60 * 1000}>
      <div className={styles.page}>
        <ToolbarRoot>
          <ToolbarTitle title={title} />
          <Stack className={toolbarStyles.tools}>
            <Stack flexWrap="nowrap" alignItems="center" justifyContent="flex-end">
              <RefreshButton />
              <MaritimeWarningTool />
              <LandWarningTool />
              <TimezoneTool />
            </Stack>
          </Stack>
        </ToolbarRoot>
        <div className={styles.content}>
          <RiskPicture copy={{ title, description }} staticOverlays={staticOverlays} />
        </div>
      </div>
    </WidgetRuntimeProvider>
  );
}
